'use client';

/**
 * @fileoverview The Discover surface's single error-handling owner (WEB-1096).
 *
 * Every Discover subscriber reads the SAME `/events/discover` cache entry
 * (`useDiscoverEventArgs`), so every subscriber sees the same RTK error
 * object, and the notification store does not dedupe. Before WEB-1096 the
 * list, the map and the mobile drawer each mounted their own
 * `useDiscoverErrorHandler`, and none of them owned "this params change was
 * the user's": a desktop rail change that 400ed was silent, Map view toasted
 * background errors, and the user-initiated flag was never cleared on
 * success.
 *
 * Now exactly one handler runs per surface, inside `DiscoverErrorHost`
 * (`components/templates/DiscoverTemplate/DiscoverErrorHost.tsx`). This module
 * holds what organisms need from it, so an organism never imports a template:
 *
 *   - {@link useDiscoverErrorHost} — the context read: `lastError` for empty
 *     states, `recordUserAction` for user actions that do not change the URL
 *     (load more, retry, show all), and the `reporter` a subscriber on a
 *     DIFFERENT cache entry (the list's cursor pages) feeds its outcome to.
 *   - {@link useReportDiscoverQuery} — feeds one query's settled outcome to a
 *     reporter. The host uses it for the shared entry, the list for its own.
 */
import * as React from 'react';

import type { DiscoverEventsQueryArgs } from '@loci/api';

import { serializeDiscoverTotalsScope } from './envelopeTotals';
import type { DiscoverErrorSummary } from './onDiscoverError';

/** The two handler entry points a query outcome is reported through. */
export interface DiscoverErrorReporter {
  /**
   * Process a settled RTK error (`undefined` once the query is clean).
   * `stale` marks an error that predates the user's request.
   */
  readonly handleError: (error: unknown, options?: { readonly stale?: boolean }) => void;
  /** Drop the user-initiated flag: the user's request settled without error. */
  readonly clearUserInitiated: () => void;
}

export interface DiscoverErrorHostValue {
  /**
   * The most recent non-abort failure the surface handler saw, or `null`.
   * Drives the request-failed / offline / rate-limited empty states.
   */
  readonly lastError: DiscoverErrorSummary | null;
  /**
   * Arm the next failure as the user's own, with nothing to revert. Only for
   * user actions that keep the URL (load more, retry, show all): their
   * failures toast without navigating. A params transition (rail, drawer,
   * map, results bar, reset) arms the host by itself, with a revert target.
   */
  readonly recordUserAction: () => void;
  /** Stable reporter for a subscriber on a cache entry the host cannot see. */
  readonly reporter: DiscoverErrorReporter;
}

export const DiscoverErrorHostContext = React.createContext<DiscoverErrorHostValue | null>(null);

/**
 * Read the surface's error host. Throws outside `<DiscoverErrorHost>` — a
 * silent fallback would bring back a second, unowned handler (WEB-1096).
 */
export function useDiscoverErrorHost(): DiscoverErrorHostValue {
  const value = React.useContext(DiscoverErrorHostContext);
  if (value === null) {
    throw new Error(
      'useDiscoverErrorHost must be used inside <DiscoverErrorHost> (WEB-1096): ' +
        'Discover has exactly one error handler per surface, mounted by DiscoverTemplate.',
    );
  }
  return value;
}

/**
 * Content key for a query's args. `useDiscoverEventArgs` re-memoizes on every
 * params change (the view included), so identity says nothing about whether
 * the REQUEST changed; the serialized args do (key-order independent).
 */
export function useDiscoverRequestKey(args: DiscoverEventsQueryArgs): string {
  return React.useMemo(() => serializeDiscoverTotalsScope(args), [args]);
}

/** The slice of an RTK query result {@link useReportDiscoverQuery} reads. */
export interface DiscoverQueryOutcome {
  /** {@link useDiscoverRequestKey} of the args this result belongs to. */
  readonly requestKey: string;
  readonly error: unknown;
  readonly isFetching: boolean;
  /** Data for the CURRENT args (`currentData`, not `data`). */
  readonly currentData: unknown;
}

/**
 * Feed one query's outcome to a reporter.
 *
 * - An error seen on the first render for a new request key predates the
 *   user's action: the request their change started cannot have answered yet.
 *   It is the cached result of an earlier failure (RTK returns that entry
 *   as-is, `isFetching: false`, until its refetch dispatches), so it is
 *   reported `stale` — it updates `lastError` but never toasts or reverts.
 * - While a refetch is in flight RTK Query 2.x keeps the previous `error`;
 *   that is skipped until the request settles.
 * - A clean result is passed through at once (`undefined` resets the guards
 *   and clears `lastError`, as before).
 * - A settled success clears the user-initiated flag, so a later background
 *   failure stays silent.
 */
export function useReportDiscoverQuery(
  reporter: DiscoverErrorReporter,
  { requestKey, error, isFetching, currentData }: DiscoverQueryOutcome,
): void {
  const { handleError, clearUserInitiated } = reporter;
  const reportedKeyRef = React.useRef(requestKey);
  // The error showing when the key changed. Compared by reference, like the
  // handler's own guards, so a re-run of this effect cannot promote it.
  const staleErrorRef = React.useRef<unknown>(undefined);
  React.useEffect(() => {
    if (reportedKeyRef.current !== requestKey) {
      reportedKeyRef.current = requestKey;
      staleErrorRef.current = error;
    }
    if (error && isFetching) return;
    handleError(error, { stale: Boolean(error) && error === staleErrorRef.current });
  }, [requestKey, error, isFetching, handleError]);
  React.useEffect(() => {
    if (isFetching || error || currentData === undefined) return;
    clearUserInitiated();
  }, [currentData, error, isFetching, clearUserInitiated]);
}
